import { SearchSelect } from '../SearchSelect';
import { Archive, ArrowLeft, Pencil, Plus, RefreshCw } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiClientError, api } from '../api';
import { Drawer, Empty, ErrorMessage, Loading, PageHeader, StatusBadge } from '../components';
import { ContactDetails } from '../ContactDetails';
import { ContactEditForm } from '../ContactEditForm';
import type {
  Assignment,
  AuditEvent,
  Company,
  Contact,
  ContactOverview,
  ExternalIdentity,
  IntegrationInstance,
} from '../types';

import { DevicesPage } from './DevicesPage';
import { usePermissions } from '../permissions';
import { auditLabel, auditFields } from './ActivityPage';

type Tab = 'overview' | 'devices' | 'contacts' | 'sites' | 'integrations' | 'activity';
export function CompanyDetailPage() {
  const can = usePermissions();
  const readMappings = can('core:external-identities:read');
  const readIntegrations = can('core:integrations:read');
  const readAudit = can('core:audit:read');
  const [loading, setLoading] = useState(true);
  const { id = '' } = useParams();
  const [company, setCompany] = useState<Company>();
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<unknown>();
  const [identities, setIdentities] = useState<ExternalIdentity[]>([]);
  const [instances, setInstances] = useState<IntegrationInstance[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(undefined);
      try {
        const nextCompany = await api<Company>(`/api/companies/${id}`, { signal });
        if (signal?.aborted) return;
        setCompany(nextCompany);
        if (tab === 'integrations' && readIntegrations) {
          const [nextInstances, nextAssignments, nextIdentities] = await Promise.all([
            api<IntegrationInstance[]>('/api/integration-instances', { signal }),
            api<Assignment[]>(`/api/entities/COMPANY/${id}/assignments`, { signal }),
            readMappings
              ? api<ExternalIdentity[]>(`/api/companies/${id}/external-identities`, { signal })
              : Promise.resolve([]),
          ]);
          if (signal?.aborted) return;
          setInstances(nextInstances);
          setAssignments(nextAssignments);
          setIdentities(nextIdentities);
        }
        if (tab === 'activity' && readAudit) {
          const result = await api<{ items: AuditEvent[] }>(
            `/api/audit?entityType=COMPANY&entityId=${id}`,
            { signal },
          );
          if (!signal?.aborted) setAudit(result.items);
        }
      } catch (caught) {
        if (!signal?.aborted) setError(caught);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [id, tab, readMappings, readIntegrations, readAudit],
  );
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);
  const conflictAware = async (work: () => Promise<unknown>) => {
    try {
      await work();
      await load();
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409) await load();
      setError(e);
    }
  };
  if (!company)
    return (
      <>
        <Link to="/companies" className="text-sm text-muted">
          ← Kunden
        </Link>
        <div className="mt-5">
          <ErrorMessage error={error} />
          {!error && <Loading>Kunde wird geladen…</Loading>}
        </div>
      </>
    );
  const archive = () => {
    if (!window.confirm(`Kunde „${company.name}“ archivieren?`)) return;
    void conflictAware(() =>
      api(`/api/companies/${id}/archive`, {
        method: 'POST',
        revision: company.revision,
        idempotencyKey: crypto.randomUUID(),
      }),
    );
  };
  const tabs: [Tab, string][] = [
    ['overview', 'Übersicht'],
    ['contacts', 'Kontakte'],
    ['sites', 'Standorte'],
    ['devices', 'Geräte'],
    ['integrations', 'Integrationen'],
    ['activity', 'Aktivität'],
  ];
  return (
    <>
      <Link
        to="/companies"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted hover:text-slate-900"
      >
        <ArrowLeft size={15} />
        Kunden
      </Link>
      <PageHeader
        title={company.name}
        description={
          company.legalName && company.legalName !== company.name
            ? company.legalName
            : (company.domains.find((domain) => domain.isPrimary)?.domain ??
              'Kundendetails und Zuordnungen')
        }
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={company.status} />
            {can('core:companies:write') && company.status !== 'ARCHIVED' && (
              <button className="button-secondary text-red-700" onClick={archive}>
                <Archive size={15} />
                Archivieren
              </button>
            )}
          </div>
        }
      />
      <ErrorMessage error={error} />
      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
        {tabs
          .filter(([key]) => key !== 'devices' || can('core:devices:read'))
          .filter(([key]) => key !== 'integrations' || readIntegrations)
          .filter(([key]) => key !== 'activity' || readAudit)
          .map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`border-b-2 px-4 py-2 text-sm font-medium ${tab === key ? 'border-accent text-accent' : 'border-transparent text-muted hover:text-slate-900'}`}
            >
              {label}
            </button>
          ))}
      </div>
      {loading && <Loading>Kundendetails werden geladen…</Loading>}
      {!loading && (
        <>
          {tab === 'devices' && <DevicesPage companyId={company.id} embedded />}{' '}
          {tab === 'overview' && (
            <Overview company={company} reload={load} setError={setError} onTab={setTab} />
          )}{' '}
          {tab === 'contacts' && <Contacts company={company} reload={load} setError={setError} />}{' '}
          {tab === 'sites' && <Sites company={company} reload={load} setError={setError} />}{' '}
          {tab === 'integrations' && (
            <IntegrationView
              company={company}
              instances={instances}
              identities={identities}
              assignments={assignments}
              reload={load}
              setError={setError}
            />
          )}{' '}
          {tab === 'activity' && <Activity events={audit} />}
        </>
      )}
    </>
  );
}

function Overview({
  company,
  reload,
  setError,
  onTab,
}: {
  company: Company;
  reload: () => Promise<void>;
  setError: (e: unknown) => void;
  onTab: (tab: Tab) => void;
}) {
  const can = usePermissions();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api(`/api/companies/${company.id}`, {
        method: 'PATCH',
        revision: company.revision,
        body: JSON.stringify({
          name: f.get('name'),
          legalName: f.get('legalName') || null,
          notes: f.get('notes') || null,
        }),
      });
      setEditing(false);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  const domain = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const element = event.currentTarget;
    const f = new FormData(element);
    setSaving(true);
    try {
      await api(`/api/companies/${company.id}/domains`, {
        method: 'POST',
        revision: company.revision,
        body: JSON.stringify({ domain: f.get('domain'), isPrimary: company.domains.length === 0 }),
      });
      element.reset();
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  const writable = can('core:companies:write') && company.status !== 'ARCHIVED';
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          className="panel p-5 text-left hover:border-accent"
          onClick={() => onTab('contacts')}
        >
          <div className="text-xs text-muted">Kontakte</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-2xl font-semibold">{company._count.contacts}</span>
            <span className="text-xs text-accent">Ansprechpartner ansehen →</span>
          </div>
        </button>
        <button className="panel p-5 text-left hover:border-accent" onClick={() => onTab('sites')}>
          <div className="text-xs text-muted">Standorte</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-2xl font-semibold">{company._count.sites}</span>
            <span className="text-xs text-accent">Standorte ansehen →</span>
          </div>
        </button>
      </div>
      <div className="grid gap-5 xl:grid-cols-[2fr_1fr]">
        <section className="panel p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2 className="font-semibold">Kundenprofil</h2>
            {writable && !editing && (
              <button className="button-secondary" onClick={() => setEditing(true)}>
                <Pencil size={14} />
                Bearbeiten
              </button>
            )}
          </div>
          {editing ? (
            <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label">Name *</span>
                <input
                  name="name"
                  defaultValue={company.name}
                  className="input w-full"
                  required
                  autoFocus
                />
              </label>
              <label>
                <span className="label">Rechtlicher Name</span>
                <input name="legalName" defaultValue={company.legalName} className="input w-full" />
              </label>
              <label className="md:col-span-2">
                <span className="label">Notizen</span>
                <textarea
                  name="notes"
                  defaultValue={company.notes}
                  className="input h-28 w-full py-2"
                />
              </label>
              <div className="flex gap-2 md:col-span-2">
                <button className="button" disabled={saving}>
                  Änderungen speichern
                </button>
                <button
                  type="button"
                  className="button-secondary"
                  disabled={saving}
                  onClick={() => setEditing(false)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <dl className="grid gap-5 md:grid-cols-2">
              <div>
                <dt className="label">Name</dt>
                <dd className="text-sm">{company.name}</dd>
              </div>
              {company.legalName && (
                <div>
                  <dt className="label">Rechtlicher Name</dt>
                  <dd className="text-sm">{company.legalName}</dd>
                </div>
              )}
              <div className="md:col-span-2">
                <dt className="label">Notizen</dt>
                <dd className="whitespace-pre-wrap text-sm">
                  {company.notes || 'Keine Notizen hinterlegt.'}
                </dd>
              </div>
            </dl>
          )}
          <details className="technical">
            <summary>Technische Metadaten</summary>
            <div className="mt-3 space-y-1 break-all">
              <p>Control-ID: {company.id}</p>
              <p>Revision: {company.revision}</p>
              <p>Erstellt: {new Date(company.createdAt).toLocaleString('de-DE')}</p>
              <p>Zuletzt geändert: {new Date(company.updatedAt).toLocaleString('de-DE')}</p>
            </div>
          </details>
        </section>
        <section className="panel h-fit p-6">
          <h2 className="font-semibold">Domains</h2>
          <p className="mt-1 text-xs text-muted">Zuordnung von E-Mail-Adressen zum Kunden.</p>
          <div className="my-4 space-y-2">
            {!company.domains.length && (
              <p className="text-sm text-muted">Keine Domains hinterlegt.</p>
            )}
            {company.domains.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-3 text-sm"
              >
                <span className="break-all">{d.domain}</span>
                {d.isPrimary && <span className="text-xs text-accent">Primär</span>}
              </div>
            ))}
          </div>
          {writable && (
            <form onSubmit={domain}>
              <label>
                <span className="label">Domain hinzufügen</span>
                <input name="domain" required className="input w-full" placeholder="example.de" />
              </label>
              <button className="button-secondary mt-3" disabled={saving}>
                <Plus size={16} />
                Hinzufügen
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function Contacts({
  company,
  reload,
  setError,
}: {
  company: Company;
  reload: () => Promise<void>;
  setError: (e: unknown) => void;
}) {
  const can = usePermissions();
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setLocalError] = useState<unknown>();
  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setSaving(true);
    setLocalError(undefined);
    try {
      await api(`/api/companies/${company.id}/contacts`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          firstName: f.get('firstName'),
          lastName: f.get('lastName'),
          jobTitle: f.get('jobTitle') || null,
          identities: f.get('email')
            ? [{ type: 'EMAIL', value: f.get('email'), isPrimary: true }]
            : [],
        }),
      });
      setShow(false);
      await reload();
    } catch (err) {
      setLocalError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <div className="panel overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-line p-5">
          <h2 className="font-semibold">Ansprechpartner</h2>
          {can('core:contacts:write') && company.status !== 'ARCHIVED' && (
            <button
              className="button"
              onClick={() => {
                setShow(true);
                setLocalError(undefined);
              }}
            >
              <Plus size={15} />
              Kontakt anlegen
            </button>
          )}
        </div>
        {!company.contacts?.length ? (
          <Empty>Noch keine Kontakte für diesen Kunden.</Empty>
        ) : (
          <div className="divide-y divide-line">
            {company.contacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} reload={reload} setError={setError} />
            ))}
          </div>
        )}
      </div>
      {show && (
        <Drawer
          title="Kontakt anlegen"
          description={`Ansprechpartner für ${company.name}`}
          onClose={() => {
            if (!saving) setShow(false);
          }}
        >
          <ErrorMessage error={error} />
          <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label">Vorname *</span>
              <input name="firstName" required className="input w-full" autoFocus />
            </label>
            <label>
              <span className="label">Nachname *</span>
              <input name="lastName" required className="input w-full" />
            </label>
            <label>
              <span className="label">Position</span>
              <input name="jobTitle" className="input w-full" />
            </label>
            <label>
              <span className="label">E-Mail</span>
              <input name="email" type="email" className="input w-full" />
            </label>
            <div className="flex gap-2">
              <button className="button" disabled={saving}>
                Kontakt anlegen
              </button>
              <button
                type="button"
                className="button-secondary"
                disabled={saving}
                onClick={() => setShow(false)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Drawer>
      )}
    </>
  );
}

function ContactCard({
  contact,
  reload,
  setError,
}: {
  contact: Contact;
  reload: () => Promise<void>;
  setError: (e: unknown) => void;
}) {
  const can = usePermissions();
  const [details, setDetails] = useState<ContactOverview>();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const writable = can('core:contacts:write') && contact.status !== 'ARCHIVED';
  const mutate = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      await reload();
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };
  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await mutate(() =>
      api(`/api/contacts/${contact.id}/identities`, {
        method: 'POST',
        body: JSON.stringify({
          type: form.get('type'),
          value: form.get('value'),
          label: form.get('label') || null,
          isPrimary: form.get('primary') === 'on',
        }),
      }),
    );
  };
  const active = contact.identities.filter((identity) => !identity.revokedAt);
  const primary = active.find((identity) => identity.isPrimary) ?? active[0];
  return (
    <details className="p-5">
      <summary className="cursor-pointer">
        <span className="ml-2 inline-flex flex-wrap items-center gap-3 align-middle">
          <span className="font-medium">{contact.displayName}</span>
          <StatusBadge status={contact.status} />
          <span className="text-xs text-muted">
            {contact.jobTitle}
            {contact.jobTitle && primary ? ' · ' : ''}
            {primary?.value}
          </span>
        </span>
      </summary>
      <div className="mt-5 space-y-4 pl-5">
        <button
          className="button-secondary"
          disabled={busy}
          onClick={() =>
            void api<ContactOverview>(`/api/contacts/${contact.id}/overview`)
              .then(setDetails)
              .catch(setError)
          }
        >
          Kontaktwege, Kunde und Synchronisierung
        </button>
        {details && (
          <Drawer
            title={contact.displayName}
            description="Kontaktverwaltung"
            onClose={() => setDetails(undefined)}
          >
            <ContactDetails
              contact={details}
              onChanged={() => {
                void reload();
              }}
            />
          </Drawer>
        )}

        {writable && (
          <button className="button-secondary" onClick={() => setEditing((value) => !value)}>
            <Pencil size={14} />
            Kontakt bearbeiten
          </button>
        )}
        {editing && (
          <ContactEditForm
            contact={contact}
            onCancel={() => setEditing(false)}
            onError={setError}
            onSaved={async () => {
              await reload();
              setEditing(false);
            }}
          />
        )}
        <div className="space-y-2">
          {!active.length && <p className="text-sm text-muted">Keine aktiven Kontaktwege.</p>}
          {active.map((identity) => (
            <div
              key={identity.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-line p-3 text-sm"
            >
              <span className="text-xs text-muted">
                {{ EMAIL: 'E-Mail', PHONE: 'Telefon', MOBILE: 'Mobil' }[identity.type] ??
                  identity.type}
              </span>
              <span className="min-w-0 flex-1 break-all">{identity.value}</span>
              {identity.isPrimary && <span className="text-xs text-accent">Primär</span>}
              {identity.verifiedAt && <span className="text-xs text-accent">Verifiziert</span>}
              {can('core:contacts:verify') &&
                contact.status !== 'ARCHIVED' &&
                !identity.verifiedAt && (
                  <button
                    className="button-secondary"
                    disabled={busy}
                    onClick={() =>
                      void mutate(() =>
                        api(`/api/contacts/${contact.id}/identities/${identity.id}/verify`, {
                          method: 'POST',
                          revision: identity.revision,
                        }),
                      )
                    }
                  >
                    Verifizieren
                  </button>
                )}
              {writable && (
                <>
                  {!identity.isPrimary && (
                    <button
                      className="button-secondary"
                      disabled={busy}
                      onClick={() =>
                        void mutate(() =>
                          api(`/api/contacts/${contact.id}/identities/${identity.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ revision: identity.revision, isPrimary: true }),
                          }),
                        )
                      }
                    >
                      Als primär
                    </button>
                  )}
                  <button
                    className="button-secondary text-red-700"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Kontaktweg „${identity.value}“ widerrufen?`))
                        void mutate(() =>
                          api(`/api/contacts/${contact.id}/identities/${identity.id}`, {
                            method: 'DELETE',
                            revision: identity.revision,
                          }),
                        );
                    }}
                  >
                    Widerrufen
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        {writable && (
          <form onSubmit={add} className="grid gap-3 border-t border-line pt-4 sm:grid-cols-2">
            <label>
              <span className="label">Kontaktart</span>
              <SearchSelect name="type" className="input w-full">
                <option value="EMAIL">E-Mail</option>
                <option value="PHONE">Telefon</option>
                <option value="MOBILE">Mobil</option>
              </SearchSelect>
            </label>
            <label>
              <span className="label">Kontaktwert *</span>
              <input
                name="value"
                className="input w-full"
                required
                placeholder="E-Mail oder Telefonnummer"
              />
            </label>
            <label>
              <span className="label">Bezeichnung</span>
              <input name="label" className="input w-full" placeholder="z. B. Geschäftlich" />
            </label>
            <label className="flex items-center gap-2 self-end py-2 text-sm">
              <input name="primary" type="checkbox" />
              Primärer Kontaktweg
            </label>
            <div>
              <button className="button-secondary" disabled={busy}>
                <Plus size={14} />
                Kontaktweg hinzufügen
              </button>
            </div>
          </form>
        )}
        <details className="technical">
          <summary>Technische Metadaten und widerrufene Kontaktwege</summary>
          <div className="mt-3 text-xs">
            <p>
              Control-ID: {contact.id} · Revision {contact.revision}
            </p>
            {contact.identities
              .filter((identity) => identity.revokedAt)
              .map((identity) => (
                <p key={identity.id} className="mt-2 break-all">
                  {identity.value} · Widerrufen
                </p>
              ))}
          </div>
        </details>
      </div>
    </details>
  );
}

function Sites({
  company,
  reload,
}: {
  company: Company;
  reload: () => Promise<void>;
  setError: (e: unknown) => void;
}) {
  const can = usePermissions();
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>();
  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setSaving(true);
    setError(undefined);
    try {
      await api(`/api/companies/${company.id}/sites`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          name: f.get('name'),
          street: f.get('street') || null,
          postalCode: f.get('postalCode') || null,
          city: f.get('city') || null,
          countryCode: f.get('countryCode') || null,
          isPrimary: company.sites?.length === 0,
        }),
      });
      setShow(false);
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <div className="panel overflow-x-auto">
        <div className="flex items-center justify-between gap-3 border-b border-line p-5">
          <h2 className="font-semibold">Standorte</h2>
          {can('core:sites:write') && company.status !== 'ARCHIVED' && (
            <button
              className="button"
              onClick={() => {
                setError(undefined);
                setShow(true);
              }}
            >
              <Plus size={15} />
              Standort anlegen
            </button>
          )}
        </div>
        {!company.sites?.length ? (
          <Empty>Noch keine Standorte für diesen Kunden.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Standort</th>
                <th>Adresse</th>
                <th>Land</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {company.sites.map((site) => (
                <tr key={site.id}>
                  <td className="font-medium">
                    {site.name}
                    {site.isPrimary && <span className="ml-2 text-xs text-accent">Primär</span>}
                  </td>
                  <td>
                    {[site.street, [site.postalCode, site.city].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </td>
                  <td>{site.countryCode ?? '—'}</td>
                  <td>
                    <StatusBadge status={site.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {show && (
        <Drawer
          title="Standort anlegen"
          description={`Neuer Standort für ${company.name}`}
          onClose={() => {
            if (!saving) setShow(false);
          }}
        >
          <ErrorMessage error={error} />
          <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2">
              <span className="label">Name *</span>
              <input name="name" required className="input w-full" autoFocus />
            </label>
            <label className="sm:col-span-2">
              <span className="label">Straße</span>
              <input name="street" className="input w-full" />
            </label>
            <label>
              <span className="label">Postleitzahl</span>
              <input name="postalCode" className="input w-full" />
            </label>
            <label>
              <span className="label">Ort</span>
              <input name="city" className="input w-full" />
            </label>
            <label>
              <span className="label">Ländercode</span>
              <input
                name="countryCode"
                className="input w-full"
                defaultValue="DE"
                minLength={2}
                maxLength={2}
              />
            </label>
            <div className="flex gap-2">
              <button className="button" disabled={saving}>
                Standort anlegen
              </button>
              <button
                type="button"
                className="button-secondary"
                disabled={saving}
                onClick={() => setShow(false)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Drawer>
      )}
    </>
  );
}

function IntegrationView({
  company,
  instances,
  identities,
  assignments,
  reload,
  setError,
}: {
  company: Company;
  instances: IntegrationInstance[];
  identities: ExternalIdentity[];
  assignments: Assignment[];
  reload: () => Promise<void>;
  setError: (e: unknown) => void;
}) {
  const can = usePermissions();
  const [syncing, setSyncing] = useState(false);
  const add = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await api('/api/external-identities', {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          entityType: 'COMPANY',
          entityId: company.id,
          provider: f.get('provider'),
          externalType: f.get('externalType'),
          externalId: f.get('externalId'),
        }),
      });
      e.currentTarget.reset();
      await reload();
    } catch (err) {
      setError(err);
    }
  };
  const setDesired = async (instance: IntegrationInstance, status: 'ENABLED' | 'DISABLED') => {
    setSyncing(true);
    try {
      const current = assignments.find((a) => a.integrationInstanceId === instance.id);
      await api('/api/assignments', {
        method: 'PUT',
        body: JSON.stringify({
          entityType: 'COMPANY',
          entityId: company.id,
          integrationInstanceId: instance.id,
          status,
          expectedVersion: current?.version,
        }),
      });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setSyncing(false);
    }
  };
  const reconcile = async () => {
    setSyncing(true);
    try {
      await api(`/api/integrations/ticket/companies/${company.id}/sync`, { method: 'POST' });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setSyncing(false);
    }
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[2fr_1fr]">
      <div className="panel overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Instanz</th>
              <th>Gewünscht</th>
              <th>Tatsächlich</th>
              <th>Zuordnung / Erfolg</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {instances.map((i) => {
              const automatic = ['helpdesk', 'device-desk'].includes(i.integration.key);
              const assignment = assignments.find((a) => a.integrationInstanceId === i.id);
              const state = assignment?.provisioning;
              const ext = identities.find((e) => e.integrationInstanceId === i.id);
              return (
                <tr key={i.id}>
                  <td>
                    <span className="font-medium">{i.name}</span>
                    <div className="text-xs text-muted">
                      {i.environment} · {i.key}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={assignment?.status ?? 'DISABLED'} />
                    {automatic && <div className="text-xs text-muted">Automatische Synchronisierung</div>}
                  </td>
                  <td>
                    <StatusBadge status={state?.status ?? 'NOT_CONFIGURED'} />
                    {state?.needsRetry && (
                      <div className="text-xs text-amber-800">Übernahme offen</div>
                    )}
                    {state?.errorMessage && (
                      <div className="max-w-md text-xs text-red-700">
                        {state.errorCode}: {state.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="text-xs">
                    {ext ? `${ext.externalType} #${ext.externalId}` : '—'}
                    <div className="text-muted">
                      {state?.lastSuccessAt
                        ? new Date(state.lastSuccessAt).toLocaleString('de-DE')
                        : 'Noch kein Erfolg'}
                    </div>
                  </td>
                  <td className="space-x-2 text-right">
                    {can('core:integrations:write') && company.status !== 'ARCHIVED' && (
                      <>
                        {!automatic && <button
                          className="button-secondary"
                          disabled={syncing}
                          onClick={() =>
                            void setDesired(
                              i,
                              assignment?.status === 'ENABLED' ? 'DISABLED' : 'ENABLED',
                            )
                          }
                        >
                          {assignment?.status === 'ENABLED' ? 'Deaktivieren' : 'Aktivieren'}
                        </button>}
                        {i.integration.key === 'helpdesk' && assignment?.status === 'ENABLED' && (
                          <button
                            className="button-secondary"
                            disabled={syncing}
                            onClick={() => void reconcile()}
                          >
                            <RefreshCw size={14} />
                            Erneut synchronisieren
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {can('core:external-identities:write') && company.status !== 'ARCHIVED' && (
        <details className="panel h-fit p-5">
          <summary className="cursor-pointer text-sm font-medium">
            Externe Verknüpfung hinzufügen
          </summary>
          <form onSubmit={add} className="mt-4 h-fit space-y-3 p-5">
            <h2 className="font-semibold">Fremd-ID verknüpfen</h2>
            <label>
              <span className="label">Provider</span>
              <input name="provider" className="input w-full" placeholder="HELPDESK" required />
            </label>
            <label>
              <span className="label">Typ</span>
              <input
                name="externalType"
                className="input w-full"
                placeholder="ORGANIZATION"
                required
              />
            </label>
            <label>
              <span className="label">Externe ID</span>
              <input name="externalId" className="input w-full" required />
            </label>
            <button className="button w-full">Verknüpfen</button>
          </form>
        </details>
      )}
    </div>
  );
}

function Activity({ events }: { events: AuditEvent[] }) {
  return (
    <div className="panel">
      {events.length === 0 ? (
        <Empty>Keine Aktivitäten vorhanden.</Empty>
      ) : (
        <div className="divide-y divide-line">
          {events.map((e) => (
            <div key={e.id} className="flex gap-4 p-4">
              <div className="w-36 shrink-0 text-xs text-muted">
                {new Date(e.createdAt).toLocaleString('de-DE')}
              </div>
              <div className="text-sm">
                <span className="font-semibold">{e.actorDisplayName ?? e.actorId}</span> ·{' '}
                {auditLabel(e)}
                <div className="mt-1 text-xs text-muted">{auditFields(e)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
